import { Component, OnInit, input, inject } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';

import { ApContentService, ApContentPipe, ApContentComponent } from '@ap/content';
import { ApAddressResource, DutchAddress } from '@ap/interface-generic';
import { ApInputComponent } from '@ap/input';
import { ApPanelComponent } from '@ap/panel';
import { ApNotificationComponent } from '@ap/notification';
import { ApDiacriticsNormalizeFormValueDirective } from '@ap/diacritics-normalize';
import { ApZipcodeInputComponent } from '@ap/input/zipcode';

import { ApReadonlyComponent } from './read-only/read-only.component';
import content from './dutch-address.content.json';
import { AddressForm } from './dutch-address.model';

const PO_BOX_REGEX = /[P|p][O|o][S|s][T|t]\s*[B|b][U|u][S|s]/;

/**
 * @ngdoc component
 * @name ApDutchAddressComponent
 * @description The dutch address component that retrieves an address based on postal code and house number.
 * @param {FormGroup<AddressForm>} [addressForm] - The form group to bind to (see {@link ApDutchAddressService})
 * @param {boolean} [poBoxAllowed=false] - The option that makes the component accept poBox addresses.
 * @param {boolean} [isRequired=false] - Whether the dutch address is a required form element
 */
@Component({
    selector: 'ap-dutch-address',
    templateUrl: './dutch-address.component.html',
    styleUrls: ['./dutch-address.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ApZipcodeInputComponent,
        ApInputComponent,
        ApPanelComponent,
        ApContentComponent,
        ApReadonlyComponent,
        ApNotificationComponent,
        ApDiacriticsNormalizeFormValueDirective,
        ApContentPipe
    ]
})
export class ApDutchAddressComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly addressResource = inject(ApAddressResource);

    /**
     * Required - The form group to bind to (see [ApDutchAddressService](https://gitlab.com/devolksbank/platforms/frontend-platform/ap-monorepos/design-system/-/blob/main/libs/components/dutch-address/src/lib/dutch-address.service.ts)
     */
    public readonly addressForm = input.required<FormGroup<AddressForm>>();

    /**
     * The option that makes the component accept poBox addresses.
     */
    public readonly poBoxAllowed = input(false);

    /**
     * Whether the dutch address is a required form element
     */
    public readonly isRequired = input(false);

    /**
     * Is manual input allowed?
     */
    public readonly manualInput = input(true);

    protected retrieved: boolean;

    protected showManualInput = true;

    protected errorMessage: string;

    protected diacriticFieldValue: any = {
        street: false,
        city: false
    };

    protected get houseNumberLabel() {
        return this.poBoxAllowed() ? 'address.houseNumber.poBoxEnabled.label' : 'address.houseNumber.label';
    }

    public ngOnInit(): void {
        this.showManualInput = this.manualInput();

        this.contentService.addContentItems(content);
        this.searchAddress(false);
        const caseInsensitiveCompare = (x: string, y: string): boolean => (x && x.toUpperCase()) === (y && y.toUpperCase());

        this.addressForm()
            .controls.postalCode.valueChanges.pipe(distinctUntilChanged(caseInsensitiveCompare))
            .subscribe((value) => {
                if (!!value) {
                    this.addressForm().controls.postalCode.setValue(value.toUpperCase());
                }
                this.searchAddress(!!value && this.retrieved);
            });
        this.addressForm()
            .controls.houseNumber.valueChanges.pipe(distinctUntilChanged())
            .subscribe((value) => this.searchAddress(!!value && this.retrieved));

        if (!this.poBoxAllowed()) {
            this.addressForm().controls.street.setValidators([
                this.addressForm().controls.street.validator,
                (control: AbstractControl) => (PO_BOX_REGEX.test(control.value) ? { poBox: true } : null)
            ]);
        }
    }

    protected onDiacriticsChange(diacriticValue: boolean, fieldName: string): void {
        this.diacriticFieldValue[fieldName] = diacriticValue;
    }

    protected get houseNumberErrors() {
        return {
            required: this.contentService.get('address.houseNumber.error.required'),
            pattern: this.contentService.get('address.houseNumber.error.pattern')
        };
    }

    protected get postalCodeErrors() {
        return {
            required: this.contentService.get('address.postalCode.error.required'),
            zipcode: this.contentService.get('address.postalCode.error.zipcode'),
            maxlength: this.contentService.get('address.postalCode.error.maxlength')
        };
    }

    protected get houseNumberSuffixErrors() {
        return {
            pattern: this.contentService.get('address.houseNumberSuffix.error.pattern'),
            maxlength: this.contentService.get('address.houseNumberSuffix.error.maxlength')
        };
    }

    protected get streetErrors() {
        return {
            required: this.contentService.get('address.street.error.required'),
            pattern: this.contentService.get('address.street.error.pattern'),
            maxlength: this.contentService.get('address.street.error.maxlength'),
            poBox: this.contentService.get('address.street.error.poBox')
        };
    }

    protected get cityErrors() {
        return {
            required: this.contentService.get('address.city.error.required'),
            pattern: this.contentService.get('address.city.error.pattern'),
            maxlength: this.contentService.get('address.city.error.maxlength')
        };
    }

    private searchAddress(doClear: boolean): void {
        const postalCode = this.addressForm().controls.postalCode;
        const houseNumber = this.addressForm().controls.houseNumber;
        if (postalCode.valid && houseNumber.valid && postalCode.value != null && houseNumber.value != null) {
            const street = this.addressForm().controls.street;
            const city = this.addressForm().controls.city;
            const retrievedControl = this.addressForm().controls.retrieved;
            this.addressResource.getAddress(postalCode.value, houseNumber.value.toString()).subscribe({
                next: (address: DutchAddress) => {
                    this.showManualInput = this.manualInput();
                    if (address.street && address.city) {
                        this.retrieved = this.showRetrieved(address.street);
                        this.errorMessage = '';
                        if (this.retrieved) {
                            street.patchValue(address.street);
                            city.patchValue(address.city);
                        } else {
                            this.clearStreetCity();
                        }

                        if (!this.poBoxAllowed() && PO_BOX_REGEX.test(address.street)) {
                            this.showManualInput = false;
                            this.showErrorMessage('address.result.manuelInput.noPoBox', doClear);
                        }
                    } else if (this.manualInput() === false) {
                        this.showErrorMessage('address.result.manuelInput.notFound', doClear);
                    } else {
                        this.showErrorMessage('address.result.notFound', doClear);
                    }
                    retrievedControl.patchValue(this.retrieved);
                },
                error: () => {
                    if (this.manualInput() === false) {
                        this.showErrorMessage('address.result.manuelInput.serviceUnavailable', doClear);
                    } else {
                        this.showErrorMessage('address.result.serviceUnavailable', doClear);
                    }
                }
            });
        }
    }

    private showErrorMessage(errorMessage: string, doClear: boolean): void {
        this.retrieved = false;
        this.errorMessage = errorMessage;
        if (doClear) {
            this.clearStreetCity();
        }
        this.addressForm().controls.retrieved.patchValue(this.retrieved);
    }

    private showRetrieved(street: string): boolean {
        return !PO_BOX_REGEX.test(street) || !!this.poBoxAllowed();
    }

    private clearStreetCity(): void {
        this.addressForm().controls.street.reset('');
        this.addressForm().controls.city.reset('');
    }
}
